import { type AwaitingOfferDto, type CaseListItemDto, datePl, formatMoney, type MeDto, type Page, type PlanningBoardDto, type ReceivableDto, shiftDate, todayInWarsaw } from '@bqcrm/shared';
import { useTaskTypeLabel } from '../../data';
import { Anchor, Badge, Group, Stack, Text, UnstyledButton } from '@mantine/core';
import { IconAlertTriangle, IconCash, IconFileInvoice, IconTruck } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';
import { api } from '../../api';
import { can } from '../../auth';
import { EmptyState, SectionCard } from '../../components/ui';
import { plural } from '../../format';
import { NextAction } from '../CasesPage';

const DAY = 86_400_000;
const daysBetween = (a: string, b: string) => Math.round((new Date(`${b}T12:00:00Z`).getTime() - new Date(`${a}T12:00:00Z`).getTime()) / DAY);
// otwarte statusy sprawy (jak widok „Aktywne” na liście spraw)
const OPEN = 'nowa,weryfikacja,oferta,umowa,realizacja,weryfikacja_koncowa';

/** Dane pulpitu zależne od uprawnień — każda sekcja pobiera tylko to, do czego użytkownik ma dostęp. */
export function useRoleData(me: MeDto) {
  const today = todayInWarsaw();
  const offers = useQuery({ queryKey: ['offers-awaiting'], queryFn: () => api<AwaitingOfferDto[]>('/offers/awaiting'), enabled: can(me, 'oferty.podglad') });
  const receivables = useQuery({ queryKey: ['receivables', 'open=true'], queryFn: () => api<ReceivableDto[]>('/finance/receivables?open=true'), enabled: can(me, 'finanse.podglad') });
  const week = useQuery({
    queryKey: ['planning-board', today, shiftDate(today, 6)],
    queryFn: () => api<PlanningBoardDto>(`/planning/board?from=${today}&to=${shiftDate(today, 6)}`),
    enabled: can(me, 'kalendarz.podglad'),
  });
  const allCases = can(me, 'sprawy.podglad', 'W');
  const action = useQuery({
    queryKey: ['cases', 'action', allCases],
    queryFn: () => api<Page<CaseListItemDto>>(`/cases?limit=100&status=${OPEN}${allCases ? '' : '&mine=true'}`),
    enabled: can(me, 'sprawy.podglad'),
  });
  // QA P27: zaległości z umów niepodpisanych nie wchodzą do „po terminie” (należność warunkowa — widać ją w Finansach)
  const overdue = (receivables.data ?? []).filter((r) => r.overdue && r.contractSigned);
  const offersSum = (offers.data ?? []).reduce((a, o) => a + (o.maxGrossGr ?? 0), 0);
  return {
    today,
    offers,
    offersSum,
    receivables,
    overdue,
    overdueSum: overdue.reduce((a, r) => a + r.amountGr - r.paidGr, 0),
    week,
    late: (action.data?.items ?? []).filter((c) => c.next?.tone === 'late'),
    allCases,
  };
}

type RoleData = ReturnType<typeof useRoleData>;

/** Oferty czekające na klienta — najbliższe końce ważności na górze. */
export function OffersAwaitingCard({ d }: { d: RoleData }) {
  const list = d.offers.data ?? [];
  return (
    <SectionCard title={`Oferty czekające na klienta (${list.length})`} icon={<IconFileInvoice size={16} />} padding="sm">
      {list.length ? (
        <Stack gap={2}>
          {list.slice(0, 6).map((o) => {
            const left = daysBetween(d.today, o.validUntil);
            const waiting = Math.max(0, daysBetween(todayInWarsaw(new Date(o.issuedAt)), d.today));
            return (
              <UnstyledButton key={o.versionId} component={Link} to={`/oferty/${o.offerId}`} className="row-link" px="sm" py={6} style={{ borderRadius: 8 }}>
                <Group justify="space-between" wrap="nowrap" gap="sm">
                  <div style={{ minWidth: 0 }}>
                    <Text size="sm" fw={600} truncate>
                      {o.clientName}
                    </Text>
                    <Text size="xs" c="dimmed">
                      {o.number} · wydana {waiting === 0 ? 'dziś' : waiting === 1 ? 'wczoraj' : `${waiting} dni temu`}
                    </Text>
                  </div>
                  <Stack gap={0} align="flex-end" style={{ flexShrink: 0 }}>
                    {o.maxGrossGr !== null && (
                      <Text size="sm" fw={600} className="tabular">
                        {formatMoney(o.maxGrossGr)}
                      </Text>
                    )}
                    <Text size="xs" c={left <= 7 ? 'red.7' : 'dimmed'} fw={left <= 7 ? 600 : 400}>
                      {left === 0 ? 'ważna do dziś' : `ważna jeszcze ${left} ${plural(left, 'dzień', 'dni', 'dni')}`}
                    </Text>
                  </Stack>
                </Group>
              </UnstyledButton>
            );
          })}
          {list.length > 6 && (
            <Text size="xs" c="dimmed" px="sm">
              i {list.length - 6} kolejnych
            </Text>
          )}
        </Stack>
      ) : (
        <EmptyState compact title="Brak ofert czekających na decyzję klienta" />
      )}
    </SectionCard>
  );
}

/** Należności po terminie — z przejściem do finansów. */
export function ReceivablesCard({ d }: { d: RoleData }) {
  return (
    <SectionCard
      title="Należności po terminie"
      icon={<IconCash size={16} />}
      padding="sm"
      actions={
        <Anchor component={Link} to="/finanse" size="sm">
          Finanse
        </Anchor>
      }
    >
      {d.overdue.length ? (
        <Stack gap={2}>
          {d.overdue.slice(0, 5).map((r) => (
            <UnstyledButton key={r.itemId} component={Link} to={`/umowy/${r.contractId}`} className="row-link" px="sm" py={6} style={{ borderRadius: 8 }}>
              <Group justify="space-between" wrap="nowrap">
                <div style={{ minWidth: 0 }}>
                  <Text size="sm" fw={600} truncate>
                    {r.clientName}
                  </Text>
                  <Text size="xs" c="dimmed" truncate>
                    {r.name} · termin {datePl(r.invoiceDue?.date ?? r.dueDate)}
                  </Text>
                </div>
                <Text size="sm" fw={600} c="var(--app-text-red)" className="tabular" style={{ flexShrink: 0 }}>
                  {formatMoney(r.amountGr - r.paidGr)}
                </Text>
              </Group>
            </UnstyledButton>
          ))}
        </Stack>
      ) : (
        <EmptyState compact title="Brak należności po terminie" />
      )}
    </SectionCard>
  );
}

/** Montaże i uruchomienia w najbliższych 7 dniach. */
export function UpcomingWorkCard({ d }: { d: RoleData }) {
  const typeLabel = useTaskTypeLabel();
  const tasks = (d.week.data?.tasks ?? [])
    .filter((t) => ['montaz', 'uruchomienie'].includes(t.taskType) && ['zaplanowane', 'w_toku'].includes(t.status))
    .map((t) => ({ t, day: t.assignments.map((a) => a.day).filter((x) => x >= d.today).sort()[0] ?? t.dateFrom ?? '' }))
    .filter((x) => x.day)
    .sort((a, b) => a.day.localeCompare(b.day));
  const team = (id: string | null) => d.week.data?.teams.find((x) => x.id === id)?.name ?? null;
  return (
    <SectionCard
      title={`Najbliższe montaże — 7 dni (${tasks.length})`}
      icon={<IconTruck size={16} />}
      padding="sm"
      actions={
        <Anchor component={Link} to="/planowanie" size="sm">
          Plan
        </Anchor>
      }
    >
      {tasks.length ? (
        <Stack gap={2}>
          {tasks.slice(0, 8).map(({ t, day }) => (
            <UnstyledButton key={t.id} component={Link} to={`/planowanie?zadanie=${t.id}`} className="row-link" px="sm" py={6} style={{ borderRadius: 8 }}>
              <Group wrap="nowrap" gap="sm">
                <Badge variant="light" color={day === d.today ? 'blue' : 'gray'} w={74} style={{ flexShrink: 0 }}>
                  {day === d.today ? 'dziś' : datePl(day).slice(0, 5)}
                </Badge>
                <div style={{ minWidth: 0 }}>
                  <Text size="sm" fw={600} truncate>
                    {t.clientName ?? t.title}
                  </Text>
                  <Text size="xs" c="dimmed" truncate>
                    {typeLabel(t)} · {team(t.teamId) ?? 'bez ekipy'}
                    {t.conditional ? ' · start zablokowany' : ''}
                  </Text>
                </div>
              </Group>
            </UnstyledButton>
          ))}
        </Stack>
      ) : (
        <EmptyState compact title="Brak montaży w najbliższym tygodniu" />
      )}
    </SectionCard>
  );
}

/** Sprawy z zaległym „następnym krokiem” (właściciel: wszystkie; inni: własne). */
export function ActionNeededCard({ d }: { d: RoleData }) {
  return (
    <SectionCard title={`${d.allCases ? 'Sprawy' : 'Moje sprawy'} wymagające działania (${d.late.length})`} icon={<IconAlertTriangle size={16} />} padding="sm">
      {d.late.length ? (
        <Stack gap={2}>
          {d.late.slice(0, 6).map((c) => (
            <UnstyledButton key={c.id} component={Link} to={`/sprawy/${c.id}`} className="row-link" px="sm" py={6} style={{ borderRadius: 8 }}>
              <Text size="sm" fw={600} truncate>
                {c.clientName} <Text span size="xs" c="dimmed" fw={400}>· {c.number}</Text>
              </Text>
              <NextAction next={c.next} size="xs" c={c} nested />
            </UnstyledButton>
          ))}
        </Stack>
      ) : (
        <EmptyState compact title="Nic nie czeka na pilne działanie" />
      )}
    </SectionCard>
  );
}
